import { createSelector } from "@reduxjs/toolkit";
import { selectHttpMcbimSpacesResult } from "../../McBim/mcbimSelectors";
import {
  selectActiveColorFilterItemFull,
  selectHttpGetRoomsCurrentFloorResult,
} from "../../Room/roomSelectors";

type ISpaceColorMapping = {
  [spaceName: string]: string; // spaceName -> colorHex
};

export const selectSpaceColorHashmapFromColorFilter = createSelector(
  [
    selectHttpMcbimSpacesResult,
    selectHttpGetRoomsCurrentFloorResult,
    selectActiveColorFilterItemFull,
  ],
  (spaces, rooms, activeColorFilterItemFull) => {
    const colorMapping: ISpaceColorMapping = {};

    if (!activeColorFilterItemFull) {
      return colorMapping;
    }

    return spaces.reduce((acc, space) => {
      const spaceName = space.name;

      const room = rooms[spaceName];

      const colorHex = activeColorFilterItemFull.getPolygonColors({
        room: room,
        space: space,
        colorSpecs: activeColorFilterItemFull.colorSpecs,
      }).fill;

      acc[spaceName] = colorHex ?? "#CCCCCC";
      return acc;
    }, colorMapping);
  }
);

export interface IColorFilterChartDatum {
  label: string;
  value: number;
  color: string;
}

export interface IColorFilterChartData {
  chartData: IColorFilterChartDatum[];
  totalCount: number;
}

export const selectColorFilterChartData = createSelector(
  [selectSpaceColorHashmapFromColorFilter, selectActiveColorFilterItemFull],
  (spaceColorMap, activeColorPanelItemFull): IColorFilterChartData => {
    if (!activeColorPanelItemFull) {
      return { chartData: [], totalCount: 0 };
    }

    const countsByColor: Record<string, number> = {};

    // räkna hur många spaces per färg
    Object.values(spaceColorMap).forEach((hex) => {
      if (!hex) return;
      countsByColor[hex] = (countsByColor[hex] ?? 0) + 1;
    });

    const chartData: IColorFilterChartDatum[] = [];
    let totalCount = 0;

    // gå igenom definierade färger i ordning från colorSpecs
    for (const spec of activeColorPanelItemFull.colorSpecs) {
      const count = countsByColor[spec.fillColor] ?? 0;
      if (count <= 0) continue;

      chartData.push({
        label: spec.explanation,
        value: count,
        color: spec.fillColor,
      });
      totalCount += count;
    }

    // ev. “övriga” färger om något ligger utanför specs
    const knownColors = new Set(activeColorPanelItemFull.colorSpecs.map((s) => s.fillColor));
    const otherCount = Object.values(spaceColorMap).reduce((acc, hex) => {
      if (!hex) return acc;
      if (knownColors.has(hex)) return acc;
      return acc + 1;
    }, 0);

    if (otherCount > 0) {
      chartData.push({
        label: "Andre / uten definert farge",
        value: otherCount,
        color: "#cccccc",
      });
      totalCount += otherCount;
    }

    return { chartData, totalCount };
  }
);
